import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageMeta from './PageMeta';
import { useTranslation } from '../contexts/LanguageContext';
import { formatDate } from '../i18n';
import './LegalDocument.css';

function TermsOfService() {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const title = t('legal.terms.title');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="legal-document">
            <PageMeta path="/terms" />
            <div className="legal-container">
                <button className="legal-back-btn" onClick={() => navigate('/')}>
                    <i className="fas fa-arrow-left"></i> {t('legal.backToGame')}
                </button>
                <h1>{title}</h1>
                <p className="last-updated">{t('legal.lastUpdated', { date: formatDate(new Date(2026, 1, 16), { dateStyle: 'long' }, language) })}</p>
                {language !== 'en' && (
                    <div className="contact-info" role="note">
                        <p>{t('legal.englishOnly')}</p>
                    </div>
                )}

                <section>
                    <h2>1. Agreement to Terms</h2>
                    <p>
                        Welcome to Word Labyrinth! These Terms of Service ("Terms") constitute a legally binding
                        agreement between you and <strong>CoreDL d.o.o.</strong>, a company registered in the Republic of Croatia
                        (OIB: 33905700350), operating as Word Labyrinth ("we," "us," or "our"), governing your access to
                        and use of our word puzzle game, including our website, mobile applications (iOS and Android),
                        and all related services (collectively, the "Service").
                    </p>
                    <p>
                        By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy.
                        If you do not agree to these Terms, you may not access or use the Service.
                    </p>
                    <p>
                        <strong>IMPORTANT:</strong> These Terms contain an arbitration clause and class action waiver
                        that affects your rights. Please read Section 14 carefully.
                    </p>
                </section>

                <section>
                    <h2>2. Eligibility</h2>
                    <p>
                        You must be at least 13 years old (or 16 in the EU) to use the Service. By using the Service,
                        you represent and warrant that:
                    </p>
                    <ul>
                        <li>You meet the minimum age requirement</li>
                        <li>You have the legal capacity to enter into these Terms</li>
                        <li>Your use of the Service complies with all applicable laws and regulations</li>
                        <li>You will provide accurate and complete information when creating an account</li>
                    </ul>
                    <p>
                        If you are under 18, you confirm that you have obtained permission from a parent or legal
                        guardian to use the Service.
                    </p>
                </section>

                <section>
                    <h2>3. Account Registration</h2>

                    <h3>3.1 Account Creation</h3>
                    <p>
                        To access certain features, you must create an account by providing a valid email address,
                        username, and password, or by using social login (Google, Facebook, Apple, or X/Twitter).
                    </p>

                    <h3>3.2 Account Security</h3>
                    <p>You are responsible for:</p>
                    <ul>
                        <li>Maintaining the confidentiality of your account credentials</li>
                        <li>All activities that occur under your account</li>
                        <li>Notifying us immediately of any unauthorized access or security breach</li>
                    </ul>

                    <h3>3.3 Account Accuracy</h3>
                    <p>
                        You must provide accurate, current, and complete information and keep your account
                        information updated.
                    </p>

                    <h3>3.4 One Account Per User</h3>
                    <p>
                        Each user may maintain only one account. Creating multiple accounts to circumvent game
                        restrictions or gain unfair advantages is prohibited.
                    </p>
                </section>

                <section>
                    <h2>4. License and Restrictions</h2>

                    <h3>4.1 Limited License</h3>
                    <p>
                        We grant you a limited, non-exclusive, non-transferable, revocable license to access and
                        use the Service for your personal, non-commercial use, subject to these Terms.
                    </p>

                    <h3>4.2 Restrictions</h3>
                    <p>You agree NOT to:</p>
                    <ul>
                        <li>Copy, modify, distribute, sell, or lease any part of the Service</li>
                        <li>Reverse engineer, decompile, or attempt to extract the source code</li>
                        <li>Use automated tools, bots, or scripts to access or interact with the Service</li>
                        <li>Attempt to gain unauthorized access to any portion of the Service</li>
                        <li>Interfere with or disrupt the Service or servers</li>
                        <li>Use the Service for any illegal or unauthorized purpose</li>
                        <li>Impersonate any person or entity</li>
                        <li>Collect or harvest information about other users</li>
                        <li>Transmit viruses, malware, or other malicious code</li>
                        <li>Exploit bugs or glitches for personal gain (report them instead)</li>
                        <li>Share, sell, or transfer your account to others</li>
                        <li>Use the Service to harass, abuse, or harm others</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Intellectual Property Rights</h2>

                    <h3>5.1 Our Intellectual Property</h3>
                    <p>
                        The Service, including all content, features, functionality, software, text, graphics, logos,
                        icons, images, and game mechanics, is owned by Word Labyrinth and is protected by copyright,
                        trademark, and other intellectual property laws.
                    </p>

                    <h3>5.2 Trademarks</h3>
                    <p>
                        "Word Labyrinth" and related logos are trademarks of Word Labyrinth. You may not use our
                        trademarks without prior written permission.
                    </p>

                    <h3>5.3 User Content</h3>
                    <p>
                        While the Service primarily generates content, any feedback, suggestions, or ideas you provide
                        to us become our property, and we may use them without restriction or compensation.
                    </p>
                </section>

                <section>
                    <h2>6. Purchases and Payments</h2>

                    <h3>6.1 Virtual Currency and Items</h3>
                    <p>
                        The Service offers in-game purchases including hint packs and ad removal. All purchases are:
                    </p>
                    <ul>
                        <li>Final and non-refundable except as required by law</li>
                        <li>Licensed, not owned - you receive only a limited license to use virtual items</li>
                        <li>Non-transferable - you cannot sell, trade, or transfer virtual items to other users</li>
                    </ul>

                    <h3>6.2 Pricing</h3>
                    <p>
                        Prices are displayed in your local currency and are subject to change without notice. Any
                        price changes will not affect purchases already in progress.
                    </p>

                    <h3>6.3 Payment Processing</h3>
                    <p>
                        Payments are processed through:
                    </p>
                    <ul>
                        <li><strong>Web:</strong> Stripe (credit/debit cards)</li>
                        <li><strong>iOS:</strong> Apple App Store In-App Purchases</li>
                        <li><strong>Android:</strong> Google Play Store In-App Billing</li>
                    </ul>
                    <p>
                        You agree to provide valid payment information and authorize us to charge your payment method.
                        Payment processing is subject to the terms of the respective payment provider.
                    </p>

                    <h3>6.4 Right of Withdrawal (EU Consumers)</h3>
                    <p>
                        Under the EU Consumer Rights Directive (2011/83/EU), you have a 14-day right of withdrawal
                        for digital content purchases. However, by completing a purchase you acknowledge and agree that:
                    </p>
                    <ul>
                        <li>The digital content (hints, ad removal, streak freezes) is delivered immediately upon purchase</li>
                        <li>You expressly consent to the immediate delivery and acknowledge that you thereby waive
                        your 14-day right of withdrawal once the digital content has been fully delivered</li>
                        <li>You will receive a confirmation of this consent via email receipt</li>
                    </ul>
                    <p>
                        If delivery fails or you did not receive the purchased items, you are entitled to a full refund.
                        Contact us at support@word-labyrinth.com within 14 days of purchase.
                    </p>

                    <h3>6.5 Refunds</h3>
                    <p>
                        Outside the withdrawal right described above, refunds may be granted in the following cases:
                    </p>
                    <ul>
                        <li>Technical failure preventing delivery of purchased content</li>
                        <li>Duplicate charges</li>
                        <li>Unauthorized transactions</li>
                        <li>As otherwise required by applicable law</li>
                    </ul>
                    <p>
                        For web purchases, contact support@word-labyrinth.com with your transaction details.
                        For mobile purchases, refund requests must be made through the respective app store:
                    </p>
                    <ul>
                        <li>iOS: Through Apple App Store support</li>
                        <li>Android: Through Google Play Store support</li>
                    </ul>

                    <h3>6.6 Invoicing and Receipts</h3>
                    <p>
                        For all web purchases processed through Stripe, you will receive a digital receipt/invoice
                        containing:
                    </p>
                    <ul>
                        <li>Seller details: CoreDL d.o.o., Croatia, OIB: 33905700350</li>
                        <li>Description of the purchased item</li>
                        <li>Price including applicable VAT</li>
                        <li>VAT rate and amount</li>
                        <li>Date of purchase and transaction reference</li>
                    </ul>
                    <p>
                        VAT is charged in accordance with EU VAT rules for digital services. The applicable VAT rate
                        is determined by your country of residence. We are registered for the EU One Stop Shop (OSS)
                        scheme for cross-border B2C digital services.
                    </p>

                    <h3>6.7 Taxes</h3>
                    <p>
                        All prices displayed include applicable VAT for EU customers. For customers outside the EU,
                        prices may not include local taxes, which will be added at checkout where required by law.
                    </p>
                </section>

                <section>
                    <h2>7. Advertising</h2>
                    <p>
                        The Service displays advertisements through Google AdSense (web) and Google AdMob (mobile).
                        By using the Service, you agree to the display of these advertisements. You may remove ads
                        by purchasing the ad-free version.
                    </p>
                    <p>
                        We are not responsible for the content of third-party advertisements or the practices of
                        advertisers.
                    </p>
                </section>

                <section>
                    <h2>8. User Conduct</h2>
                    <p>You agree to use the Service in compliance with all applicable laws and these Terms. Prohibited conduct includes:</p>
                    <ul>
                        <li>Cheating or using unauthorized third-party software</li>
                        <li>Exploiting bugs or glitches for advantage</li>
                        <li>Harassment, bullying, or toxic behavior toward other users</li>
                        <li>Using offensive, inappropriate, or misleading usernames</li>
                        <li>Spamming or sending unsolicited messages</li>
                        <li>Attempting to defraud us or other users</li>
                        <li>Violating any person's rights, including intellectual property rights</li>
                    </ul>
                </section>

                <section>
                    <h2>9. Termination and Suspension</h2>

                    <h3>9.1 Termination by You</h3>
                    <p>
                        You may terminate your account at any time by contacting us at support@word-labyrinth.com
                        or using the account deletion feature in the app settings.
                    </p>

                    <h3>9.2 Termination by Us</h3>
                    <p>
                        We reserve the right to suspend or terminate your account and access to the Service,
                        with or without notice, for any reason, including:
                    </p>
                    <ul>
                        <li>Violation of these Terms</li>
                        <li>Fraudulent activity or payment disputes</li>
                        <li>Prolonged inactivity</li>
                        <li>At our discretion to protect the Service or other users</li>
                    </ul>

                    <h3>9.3 Effect of Termination</h3>
                    <p>
                        Upon termination:
                    </p>
                    <ul>
                        <li>Your license to use the Service ends immediately</li>
                        <li>You will lose access to your account and all associated content</li>
                        <li>Virtual items and currency will be forfeited without refund</li>
                        <li>Sections of these Terms that by their nature should survive will remain in effect</li>
                    </ul>
                </section>

                <section>
                    <h2>10. Changes to the Service</h2>
                    <p>
                        We reserve the right to:
                    </p>
                    <ul>
                        <li>Modify, suspend, or discontinue any part of the Service at any time</li>
                        <li>Change game features, mechanics, or virtual item attributes</li>
                        <li>Update the Service for technical, security, or legal reasons</li>
                    </ul>
                    <p>
                        We will not be liable to you or any third party for any modification, suspension, or
                        discontinuation of the Service.
                    </p>
                </section>

                <section>
                    <h2>11. Disclaimers</h2>
                    <p>
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
                        EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                    </p>
                    <ul>
                        <li>Implied warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
                        <li>That the Service will be uninterrupted, secure, or error-free</li>
                        <li>That defects will be corrected</li>
                        <li>That the Service is free of viruses or harmful components</li>
                    </ul>
                    <p>
                        Your use of the Service is at your sole risk. Some jurisdictions do not allow disclaimers
                        of implied warranties, so some of the above may not apply to you.
                    </p>
                </section>

                <section>
                    <h2>12. Limitation of Liability</h2>
                    <p>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, WORD LABYRINTH SHALL NOT BE LIABLE FOR:
                    </p>
                    <ul>
                        <li>Indirect, incidental, special, consequential, or punitive damages</li>
                        <li>Loss of profits, revenue, data, use, or other intangible losses</li>
                        <li>Damages resulting from unauthorized access, use, or alteration of your content</li>
                        <li>Damages relating to third-party services or content</li>
                    </ul>
                    <p>
                        OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF (A) $100 OR (B) THE AMOUNT YOU PAID
                        TO US IN THE TWELVE MONTHS PRECEDING THE CLAIM.
                    </p>
                    <p>
                        Some jurisdictions do not allow limitations on liability, so some of the above may not apply to you.
                    </p>
                </section>

                <section>
                    <h2>13. Indemnification</h2>
                    <p>
                        You agree to indemnify, defend, and hold harmless Word Labyrinth and its officers, directors,
                        employees, and agents from and against any claims, liabilities, damages, losses, and expenses,
                        including reasonable attorneys' fees, arising out of or in any way connected with:
                    </p>
                    <ul>
                        <li>Your violation of these Terms</li>
                        <li>Your use of the Service</li>
                        <li>Your violation of any rights of another party</li>
                        <li>Your violation of any applicable laws or regulations</li>
                    </ul>
                </section>

                <section>
                    <h2>14. Dispute Resolution</h2>

                    <h3>14.1 Informal Resolution</h3>
                    <p>
                        Before filing a claim, you agree to contact us at support@word-labyrinth.com to attempt to
                        resolve the dispute informally. We will attempt to resolve disputes in good faith.
                    </p>

                    <h3>14.2 Legal Proceedings</h3>
                    <p>
                        If informal resolution fails, disputes may be resolved through:
                    </p>
                    <ul>
                        <li>Mediation services in accordance with Croatian law</li>
                        <li>Croatian courts with jurisdiction over the matter</li>
                        <li>For EU consumers: The European Commission's Online Dispute Resolution platform at
                        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer"> https://ec.europa.eu/consumers/odr</a></li>
                    </ul>

                    <h3>14.3 EU Consumer Rights</h3>
                    <p>
                        For users in the European Union, you retain all mandatory consumer rights under EU law,
                        including the right to bring proceedings in your country of residence.
                    </p>
                </section>

                <section>
                    <h2>15. Governing Law and Jurisdiction</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of the Republic of Croatia,
                        without regard to its conflict of law provisions.
                    </p>
                    <p>
                        For users in the European Union, mandatory EU consumer protection laws and regulations apply,
                        including but not limited to the EU Consumer Rights Directive and GDPR. For users outside the EU,
                        mandatory local consumer protection laws may also apply.
                    </p>
                    <p>
                        Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the
                        courts of Croatia.
                    </p>
                </section>

                <section>
                    <h2>16. Changes to These Terms</h2>
                    <p>
                        We may update these Terms from time to time. Changes will be effective when posted with a
                        new "Last Updated" date. Material changes will be communicated through:
                    </p>
                    <ul>
                        <li>In-game notifications</li>
                        <li>Email (if you have an account)</li>
                        <li>A prominent notice on our website</li>
                    </ul>
                    <p>
                        Your continued use of the Service after changes become effective constitutes acceptance of
                        the updated Terms. If you do not agree to the updated Terms, you must stop using the Service.
                    </p>
                </section>

                <section>
                    <h2>17. Severability</h2>
                    <p>
                        If any provision of these Terms is found to be invalid or unenforceable, the remaining
                        provisions will remain in full force and effect, and the invalid provision will be replaced
                        with a valid provision that most closely approximates the intent of the original.
                    </p>
                </section>

                <section>
                    <h2>18. Waiver</h2>
                    <p>
                        Our failure to enforce any right or provision of these Terms will not be considered a waiver
                        of those rights. Any waiver must be in writing and signed by us.
                    </p>
                </section>

                <section>
                    <h2>19. Entire Agreement</h2>
                    <p>
                        These Terms, together with our Privacy Policy and any other policies or guidelines posted on
                        the Service, constitute the entire agreement between you and Word Labyrinth regarding the Service.
                    </p>
                </section>

                <section>
                    <h2>20. Contact Information</h2>
                    <p>
                        If you have questions about these Terms, please contact us:
                    </p>
                    <div className="contact-info">
                        <p><strong>Company:</strong> CoreDL d.o.o.</p>
                        <p><strong>OIB:</strong> 33905700350</p>
                        <p><strong>Address:</strong> Gajnički vidikovac II. 14/2, 10090 Zagreb, Croatia</p>
                        <p><strong>Email:</strong> support@word-labyrinth.com</p>
                        <p><strong>Website:</strong> <a href="https://www.word-labyrinth.com" target="_blank" rel="noopener noreferrer">www.word-labyrinth.com</a></p>
                    </div>
                </section>

                <section className="legal-footer">
                    <p>
                        By using Word Labyrinth, you acknowledge that you have read, understood, and agree to be
                        bound by these Terms of Service.
                    </p>
                    <p>
                        Thank you for playing Word Labyrinth!
                    </p>
                </section>
            </div>
        </div>
    );
}

export default TermsOfService;
